'use client'

import { useState, useEffect, useCallback } from 'react'
import { apiFetch } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { Calendar, CheckCircle2, AlertCircle, ChevronDown, ChevronUp, ClipboardList } from 'lucide-react'
import { GlassCard } from '@/components/ui/glass-card'
import { KpiTile } from '@/components/ui/kpi-tile'
import { StatusPill, statusTone } from '@/components/ui/status-pill'
import { SectionHeader } from '@/components/ui/section-header'
import { PageShell } from '@/components/ui/page-shell'
import { TabLoader, EmptyState } from '@/components/ui/loading'
import { cn } from '@/lib/utils'

type CardTone = 'default' | 'orange' | 'green' | 'blue' | 'red' | 'amber' | 'violet' | 'cyan'
const toCardTone = (t: ReturnType<typeof statusTone>): CardTone => (t === 'neutral' ? 'default' : t)

interface Project {
  id: string
  name: string
}

interface Task {
  id: string
  title: string
  description: string | null
  status: string
  priority: string
  dueDate: string | null
  projectId: string | null
  project: Project | null
  completedAt: string | null
}

interface EmployeeTasksProps {
  onRefresh: () => void
}

const FILTERS: { id: string; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'PENDING', label: 'Pending' },
  { id: 'IN_PROGRESS', label: 'In Progress' },
  { id: 'COMPLETED', label: 'Done' },
]

function priorityTone(p: string): 'red' | 'orange' | 'amber' | 'neutral' {
  if (p === 'CRITICAL') return 'red'
  if (p === 'HIGH') return 'orange'
  if (p === 'MEDIUM') return 'amber'
  return 'neutral'
}

export function EmployeeTasks({ onRefresh }: EmployeeTasksProps) {
  const [tasks, setTasks] = useState<Task[]>([])
  const [loading, setLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/tasks${filterStatus && filterStatus !== 'all' ? `?status=${filterStatus}` : ''}`)
      const data = await res.json()
      if (data.tasks) setTasks(data.tasks)
    } catch (error) {
      console.error('Error fetching tasks:', error)
      toast.error('Failed to fetch tasks')
    } finally {
      setLoading(false)
    }
  }, [filterStatus])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleStatusChange = async (taskId: string, newStatus: string) => {
    try {
      const res = await apiFetch(`/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      })
      toast.success('Task status updated')
      fetchData()
      onRefresh()
    } catch (e: any) {
      toast.error(e.message || 'Failed to update task')
    }
  }

  const isOverdue = (dueDate: string | null, status: string) => {
    if (!dueDate || status === 'COMPLETED' || status === 'CANCELLED') return false
    return new Date(dueDate) < new Date()
  }

  const taskStats = {
    total: tasks.length,
    pending: tasks.filter(t => t.status === 'PENDING').length,
    inProgress: tasks.filter(t => t.status === 'IN_PROGRESS').length,
    completed: tasks.filter(t => t.status === 'COMPLETED').length,
  }
  const completionPct = taskStats.total > 0 ? Math.round((taskStats.completed / taskStats.total) * 100) : 0

  return (
    <PageShell>
      <SectionHeader
        icon={ClipboardList}
        title='My Tasks'
        description='View and update your assigned tasks'
      />

      {loading ? (
        <TabLoader />
      ) : (
        <>
      {/* KPI Row */}
      <div className='grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4'>
        <KpiTile label='Total' value={taskStats.total} icon={ClipboardList} tone='default' />
        <KpiTile label='Pending' value={taskStats.pending} icon={AlertCircle} tone='amber' />
        <KpiTile label='In Progress' value={taskStats.inProgress} icon={Calendar} tone='blue' />
        <KpiTile label='Completed' value={taskStats.completed} icon={CheckCircle2} tone='green' />
      </div>

      {/* Overall progress */}
      <GlassCard tone='orange'>
        <div className='flex items-center justify-between mb-2'>
          <span className='text-sm text-neutral-700 dark:text-white/70 font-medium'>Overall completion</span>
          <span className='text-sm text-neutral-900 dark:text-white/90 font-semibold tabular-nums'>{completionPct}%</span>
        </div>
        <Progress value={completionPct} className='h-2' />
      </GlassCard>

      {/* Filter pills */}
      <div className='flex items-center gap-1.5 overflow-x-auto -mx-1 px-1 pb-1'>
        {FILTERS.map(f => {
          const active = filterStatus === f.id
          return (
            <button
              key={f.id}
              onClick={() => setFilterStatus(f.id)}
              className={cn(
                'shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium transition-all',
                'ring-1 ring-inset',
                active
                  ? 'bg-gradient-to-r from-orange-500/20 to-orange-500/5 text-orange-700 dark:text-orange-200 ring-orange-500/30'
                  : 'bg-neutral-50 dark:bg-white/5 text-neutral-600 dark:text-white/60 ring-neutral-200 dark:ring-white/10 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-white/10'
              )}
            >
              {f.label}
            </button>
          )
        })}
      </div>

      {tasks.length === 0 ? (
        <GlassCard>
          <EmptyState
            icon={CheckCircle2}
            title='No tasks here'
            description='Nothing matches this filter right now.'
          />
        </GlassCard>
      ) : (
        <div className='space-y-3'>
          {tasks.map((task) => {
            const isExpanded = expandedId === task.id
            const overdue = isOverdue(task.dueDate, task.status)
            return (
              <GlassCard key={task.id} tone={overdue ? 'red' : toCardTone(statusTone(task.status))}>
                <div className='flex flex-col sm:flex-row sm:items-start justify-between gap-3'>
                  <div className='flex-1 min-w-0 space-y-2'>
                    <div className='flex items-center gap-2 flex-wrap'>
                      <p className='text-sm font-medium text-neutral-900 dark:text-white/90'>{task.title}</p>
                      <StatusPill tone={statusTone(task.status)}>{task.status?.replace('_', ' ') || 'UNKNOWN'}</StatusPill>
                      <StatusPill tone={priorityTone(task.priority)}>{task.priority}</StatusPill>
                      {overdue && <StatusPill tone='red'><AlertCircle className='h-3 w-3' />Overdue</StatusPill>}
                    </div>
                    <div className='flex items-center gap-3 text-xs text-neutral-500 dark:text-white/50 flex-wrap'>
                      {task.project && <span>Project: <span className='text-neutral-700 dark:text-white/70'>{task.project.name}</span></span>}
                      {task.dueDate && (
                        <span className={cn('flex items-center gap-1 tabular-nums', overdue && 'text-rose-600 dark:text-rose-300')}>
                          <Calendar className='h-3 w-3' />
                          Due {(() => { const d = new Date(task.dueDate as string); return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}` })()}
                        </span>
                      )}
                    </div>
                    {task.description && (
                      <div>
                        <p className={cn('text-sm text-neutral-600 dark:text-white/60', isExpanded ? 'whitespace-pre-wrap' : 'line-clamp-2')}>
                          {task.description}
                        </p>
                        {task.description.length > 100 && (
                          <Button
                            variant='ghost'
                            size='sm'
                            onClick={() => setExpandedId(isExpanded ? null : task.id)}
                            className='text-orange-600 dark:text-orange-300 hover:text-orange-700 dark:hover:text-orange-200 hover:bg-orange-500/10 h-6 px-2 mt-1 text-xs'
                          >
                            {isExpanded ? (
                              <><ChevronUp className='h-3 w-3 mr-1' /> Show less</>
                            ) : (
                              <><ChevronDown className='h-3 w-3 mr-1' /> Show more</>
                            )}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className='shrink-0'>
                    <Select
                      value={task.status}
                      onValueChange={(value) => handleStatusChange(task.id, value)}
                      disabled={task.status === 'COMPLETED' || task.status === 'CANCELLED'}
                    >
                      <SelectTrigger className='w-[140px] h-9'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='PENDING'>Pending</SelectItem>
                        <SelectItem value='IN_PROGRESS'>In Progress</SelectItem>
                        <SelectItem value='COMPLETED'>Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </GlassCard>
            )
          })}
        </div>
      )}
        </>
      )}
    </PageShell>
  )
}
